/**
 * Public track.* API. Call from useEffect, event handlers, or imperative code —
 * NEVER from component render bodies (would fire on every re-render).
 *
 * All calls are no-ops until `initAnalytics(userId)` has been invoked. This
 * avoids dropping events on the floor before auth resolves and avoids weird
 * partial states.
 */
import type { UserEventInput, UserEventType } from '@portal/shared';
import type { SessionAPI } from './session.js';
import type { QueueAPI } from './queue.js';

interface RuntimeRefs {
  session: SessionAPI;
  queue: QueueAPI;
  currentRoute: string;
}

let runtime: RuntimeRefs | null = null;

export function bindRuntime(refs: RuntimeRefs): void {
  runtime = refs;
}

export function unbindRuntime(): void {
  runtime = null;
}

function emit(
  type: UserEventType,
  extras: Partial<UserEventInput> = {},
): void {
  if (!runtime) return;
  const { sessionId, freshlyStarted } = runtime.session.getOrRefreshSessionId();
  const clientTs = new Date().toISOString();
  if (freshlyStarted) {
    runtime.queue.enqueue({
      clientTs,
      sessionId,
      type: 'session_start',
      route: runtime.currentRoute,
    });
  }
  runtime.queue.enqueue({
    clientTs,
    sessionId,
    type,
    route: runtime.currentRoute,
    ...extras,
  });
}

interface InteractionOpts {
  jobId?: string;
  targetType?: string;
  targetId?: string;
  /** Arbitrary structured detail. Lands in the event's `meta` field. */
  extra?: Record<string, unknown>;
}

export const track = {
  pageView(route: string, opts?: { jobId?: string }) {
    if (runtime) runtime.currentRoute = route;
    emit('page_view', { jobId: opts?.jobId });
  },
  interaction(name: string, opts?: InteractionOpts) {
    emit('interaction', {
      name,
      jobId: opts?.jobId,
      targetType: opts?.targetType,
      targetId: opts?.targetId,
      meta: opts?.extra,
    });
  },
  /** Called by the heartbeat scheduler — not normally called from app code. */
  idle(activeMs: number) {
    if (!Number.isFinite(activeMs) || activeMs <= 0) return;
    emit('idle_ping', { activeMs });
  },
};
